import React from "react";

/**
 * SETTINGS — the knobs that are the same argument for both call automations.
 *
 * Deliberately the dull tab. Everything here has a defensible default and most
 * of it will never be touched; what it must not do is hide a setting whose
 * default Gabriel would disagree with if he knew it was there. So each one says
 * what it is FOR rather than restating its own label, and the two that can
 * silence mail entirely — the daily cap and the quiet window — say what happens
 * when they bite.
 *
 * THE SEND WINDOW IS ONLY HERE FOR THE RESCUE, and its absence from the
 * reminders is worth the sentence it costs: a reminder's hour is chosen by the
 * call, and deferring "your call is in an hour" to the next working morning
 * delivers it after the call. A rescue is still true tomorrow, so it waits.
 */

const DAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

function Field({ label, hint, children }) {
  return (
    <label className="crm-auto-field">
      <span className="crm-label">{label}</span>
      <span className="crm-auto-field__row">{children}</span>
      {hint && <span className="crm-hint">{hint}</span>}
    </label>
  );
}

export default function CallSettingsTab({ config, patch, kind }) {
  const template = config.template || {};
  const limits = config.limits || {};
  const schedule = config.schedule || null;
  const isRescue = kind === "call_no_show";

  const toggleDay = (value) => {
    const days = schedule.days || [];
    patch("schedule", {
      days: days.includes(value) ? days.filter((d) => d !== value) : [...days, value].sort(),
    });
  };

  return (
    <div className="crm-auto-settings">
      <section className="crm-card">
        <h3>How it arrives</h3>

        <Field
          label="Thread"
          hint={
            template.thread === "continue"
              ? "Replies inside the newest thread that person has actually written in, so it lands in the real conversation rather than under a booking notification. With nobody having written yet, it uses the newest thread you started; with no thread at all, it opens one with the subject below."
              : "Starts a fresh thread with the subject you wrote. A new thread per email is what a sequencer looks like from the other side."
          }
        >
          <select
            className="crm-select"
            value={template.thread || "continue"}
            onChange={(event) => patch("template", { thread: event.target.value })}
          >
            <option value="continue">Reply in the latest thread</option>
            <option value="new">Start a new thread</option>
          </select>
        </Field>

        <label className="crm-auto-inline">
          <input
            type="checkbox"
            checked={!!template.signature}
            onChange={(event) => patch("template", { signature: event.target.checked })}
          />
          <span>Add your Gmail signature</span>
        </label>

        <label className="crm-auto-inline">
          <input
            type="checkbox"
            checked={!!template.slack_notify}
            onChange={(event) => patch("template", { slack_notify: event.target.checked })}
          />
          <span>Post to Slack when one goes out</span>
        </label>
        {template.slack_notify && (
          <Field label="Channel">
            <input
              className="crm-input"
              value={template.slack_channel || ""}
              onChange={(event) => patch("template", { slack_channel: event.target.value })}
            />
          </Field>
        )}

        {!isRescue && (
          <Field
            label="When we do not know their country"
            hint={
              template.unknown_timezone === "block"
                ? "Those calls get no reminders at all. Nothing is ever said in the wrong clock, at the cost of the reminder."
                : "The time is named in your own zone, with the zone said out loud so nothing is ambiguous. Their invite already shows their local time."
            }
          >
            <select
              className="crm-select"
              value={template.unknown_timezone || "calendar"}
              onChange={(event) => patch("template", { unknown_timezone: event.target.value })}
            >
              <option value="calendar">Use your zone and name it</option>
              <option value="block">Hold the reminder back</option>
            </select>
          </Field>
        )}
      </section>

      {isRescue && schedule && (
        <section className="crm-card">
          <h3>When a rescue may go out</h3>
          <p className="crm-hint">
            In their clock when we know it. A rescue is still true tomorrow, so an evening no-show is
            answered the next morning rather than at 21:40. Reminders have no window for the opposite
            reason: their hour is chosen by the call.
          </p>

          <div className="crm-auto-days">
            {DAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                className={`crm-auto-day${(schedule.days || []).includes(day.value) ? " is-on" : ""}`}
                onClick={() => toggleDay(day.value)}
              >
                {day.label}
              </button>
            ))}
          </div>

          <Field label="Between">
            <input
              className="crm-input"
              type="time"
              value={schedule.window?.start || "08:00"}
              onChange={(event) =>
                patch("schedule", { window: { ...schedule.window, start: event.target.value } })
              }
            />
            <span>and</span>
            <input
              className="crm-input"
              type="time"
              value={schedule.window?.end || "19:00"}
              onChange={(event) =>
                patch("schedule", { window: { ...schedule.window, end: event.target.value } })
              }
            />
          </Field>

          <label className="crm-auto-inline">
            <input
              type="checkbox"
              checked={schedule.in_lead_timezone !== false}
              onChange={(event) => patch("schedule", { in_lead_timezone: event.target.checked })}
            />
            <span>Use their timezone when we know it</span>
          </label>
        </section>
      )}

      <section className="crm-card">
        <h3>Limits</h3>

        <Field
          label="Most in a day"
          hint={
            isRescue
              ? "Over the limit a rescue is blocked rather than held: pushed past tomorrow it is no longer a rescue, it is a chase, and the stale sequence writes better chases."
              : "Over the limit a reminder is blocked rather than held: pushed past its moment it is an email about a call that has already happened."
          }
        >
          <input
            className="crm-input"
            type="number"
            min="1"
            max="200"
            value={limits.daily_cap ?? 10}
            onChange={(event) => patch("limits", { daily_cap: Number(event.target.value) || 1 })}
          />
        </Field>

        <Field
          label="Stay quiet if you wrote to them within"
          hint={
            isRescue
              ? "If you have been in touch at all since a call they missed, you are handling it, and an automatic apology underneath your own is the thing this guards against. Zero switches it off."
              : "Sending the deck three days out is no reason to cancel “see you tomorrow”, but “let's push to Friday” an hour out is. Zero switches it off."
          }
        >
          <input
            className="crm-input"
            type="number"
            min="0"
            max={isRescue ? 336 : 168}
            value={limits.human_touch_hours ?? 24}
            onChange={(event) =>
              patch("limits", { human_touch_hours: Number(event.target.value) || 0 })
            }
          />
          <span>hours</span>
        </Field>

        {!isRescue && (
          <>
            {/*
              REMINDERS ONLY, for now. The no-show rescue has the same gap — a
              lead who mails "sorry, I got pulled into something" ten minutes
              after the call should not then receive an automatic apology — but
              its config carries no such field, and zod would strip one written
              here rather than save it.
            */}
            <Field
              label="Stay quiet if they wrote to you within"
              hint="The one that costs you something: somebody replying “looking forward to it” has confirmed the call, and this silences the reminder for them too. It is here because “can we move this?” is the same shape to a rule that cannot read, and that is the message a reminder must never talk over. Zero switches it off."
            >
              <input
                className="crm-input"
                type="number"
                min="0"
                max="168"
                value={limits.reply_touch_hours ?? 24}
                onChange={(event) =>
                  patch("limits", { reply_touch_hours: Number(event.target.value) || 0 })
                }
              />
              <span>hours</span>
            </Field>

            <Field
              label="Skip a reminder booked within"
              hint="Nobody has forgotten a meeting they made this morning. A call booked today for tomorrow afternoon still puts the day-before reminder a few hours after the booking, and this is what stops it going. Measured from the booking to the reminder, not to the call. Zero switches it off."
            >
              <input
                className="crm-input"
                type="number"
                min="0"
                max="168"
                value={limits.booked_gap_hours ?? 18}
                onChange={(event) =>
                  patch("limits", { booked_gap_hours: Number(event.target.value) || 0 })
                }
              />
              <span>hours of it</span>
            </Field>

            <Field
              label="Plan calls up to"
              hint="A call further out than this is planned nearer the time, so the copy it carries is the copy in force then rather than whatever was written four months earlier."
            >
              <input
                className="crm-input"
                type="number"
                min="1"
                max="180"
                value={limits.horizon_days ?? 21}
                onChange={(event) => patch("limits", { horizon_days: Number(event.target.value) || 21 })}
              />
              <span>days ahead</span>
            </Field>

            <Field
              label="Skip a reminder with less than"
              hint="Below this, a reminder would land looking like a mistake, so it is skipped and the log says so rather than sent late."
              >
              <input
                className="crm-input"
                type="number"
                min="0"
                max="1440"
                value={limits.min_notice_minutes ?? 20}
                onChange={(event) =>
                  patch("limits", { min_notice_minutes: Number(event.target.value) || 0 })
                }
              />
              <span>minutes of notice</span>
            </Field>
          </>
        )}

        <Field
          label="Never write to these domains"
          hint="One per line. Anything here is skipped before the copy is even chosen."
        >
          <textarea
            className="crm-auto-domains"
            rows={3}
            value={(limits.blocked_domains || []).join("\n")}
            onChange={(event) =>
              patch("limits", {
                blocked_domains: event.target.value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean),
              })
            }
          />
        </Field>
      </section>
    </div>
  );
}
